// src/components/billing/CreatePurchaseInvoiceModal.jsx - Updated with product-level GST and CGST/SGST
import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  Timestamp,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getProducts } from '../../services/inventoryService';
import { updateProductStockForPurchase } from '../../services/inventoryService';

// Supplier Management Modal Component
const SupplierModal = ({ isOpen, onClose, onSave, editingSupplier = null }) => {
  const { vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  const [loading, setLoading] = useState(false);
  const [supplierData, setSupplierData] = useState({
    name: '',
    contact: '',
    address: '',
    gstNumber: '',
    email: ''
  });

  useEffect(() => {
    if (editingSupplier) {
      setSupplierData(editingSupplier);
    } else {
      setSupplierData({
        name: '',
        contact: '',
        address: '',
        gstNumber: '',
        email: ''
      });
    }
  }, [editingSupplier]);

  const handleInputChange = (field, value) => {
    setSupplierData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!supplierData.name.trim()) {
      showAlert('Supplier name is required', 'error');
      return;
    }

    setLoading(true);
    try {
      const supplierDoc = {
        ...supplierData,
        vendorId: vendorProfile.uid,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      if (editingSupplier && editingSupplier.id) {
        // Update existing supplier
        await updateDoc(doc(db, 'suppliers', editingSupplier.id), {
          ...supplierDoc,
          createdAt: editingSupplier.createdAt // Keep original creation date
        });
        showAlert('Supplier updated successfully', 'success');
      } else {
        // Add new supplier
        const docRef = await addDoc(collection(db, 'suppliers'), supplierDoc);
        supplierDoc.id = docRef.id;
        showAlert('Supplier added successfully', 'success');
      }

      onSave(supplierDoc);
      onClose();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showAlert('Failed to save supplier', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                    {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Supplier Name *</label>
                      <input
                        type="text"
                        required
                        value={supplierData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Enter supplier company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                      <input
                        type="tel"
                        value={supplierData.contact}
                        onChange={(e) => handleInputChange('contact', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="+91 9876543210"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={supplierData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="supplier@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">GST Number</label>
                      <input
                        type="text"
                        value={supplierData.gstNumber}
                        onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Enter GST number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea
                        rows={3}
                        value={supplierData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                        placeholder="Supplier address"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="inline-flex w-full justify-center rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
              >
                {loading ? 'Saving...' : (editingSupplier ? 'Update' : 'Add')} Supplier
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const CreatePurchaseInvoiceModal = ({
  onClose,
  onSubmit,
  isModal = true,
  onNavigateToAddProduct
}) => {
  const { currentUser, vendorProfile } = useAuth();
  const { showAlert } = useNotification();

  const [step, setStep] = useState(1); // 1: Form, 2: Preview

  // Generate invoice number once and store it
  const [invoiceNumber] = useState(() => {
    const prefix = 'PUR';
    const date = new Date();
    const timestamp = date.getTime().toString().slice(-6);
    return `${prefix}-${date.getFullYear()}-${timestamp}`;
  });

  // Form state
  const [formData, setFormData] = useState({
    supplierName: '',
    supplierContact: '',
    supplierAddress: '',
    supplierGST: '',
    supplierEmail: '',
    invoiceNumber: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    paymentTerms: 'immediate',
    notes: '',
    gstType: 'with_gst'
  });

  // Items state - Updated with individual GST rates (removed manual type)
  const [items, setItems] = useState([{
    productId: '',
    productName: '',
    quantity: 1,
    rate: 0,
    gstRate: 18, // Individual GST rate per item
    amount: 0,
    hsnCode: ''
  }]);

  // Product selection
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Supplier management
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  // HSN code suggestions
  const [hsnSuggestions, setHsnSuggestions] = useState([]);
  const [showHsnDropdown, setShowHsnDropdown] = useState(false);
  const [activeHsnIndex, setActiveHsnIndex] = useState(null);

  // Common HSN codes for auto parts
  const commonHsnCodes = [
    { code: '8708', description: 'Parts and accessories of motor vehicles' },
    { code: '8409', description: 'Parts for engines' },
    { code: '8511', description: 'Electrical ignition or starting equipment' },
    { code: '8512', description: 'Electrical lighting or signaling equipment' },
    { code: '8707', description: 'Bodies for motor vehicles' },
    { code: '4011', description: 'New pneumatic tyres, of rubber' },
    { code: '4013', description: 'Inner tubes, of rubber' },
    { code: '8536', description: 'Electrical apparatus for switching circuits' },
    { code: '8544', description: 'Insulated wire, cable and other conductors' },
    { code: '2710', description: 'Petroleum oils and preparations' }
  ];

  // Load products and suppliers
  useEffect(() => {
    const loadProducts = async () => {
      if (!vendorProfile?.uid) return;

      try {
        setLoading(true);
        const result = await getProducts(vendorProfile.uid, { pageSize: 1000 });
        setProducts(result.products || []);
        setFilteredProducts(result.products || []);
      } catch (error) {
        console.error('Error loading products:', error);
        showAlert('Failed to load products', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    loadSuppliers();
  }, [vendorProfile?.uid]);

  const loadSuppliers = async () => {
    try {
      if (vendorProfile?.uid) {
        const suppliersQuery = query(
          collection(db, 'suppliers'),
          where('vendorId', '==', vendorProfile.uid)
        );
        const suppliersSnapshot = await getDocs(suppliersQuery);
        const suppliersList = suppliersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setSuppliers(suppliersList);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const handleSupplierSearch = (value) => {
    setFormData(prev => ({ ...prev, supplierName: value }));

    if (value.trim()) {
      const filtered = suppliers.filter(supplier =>
        supplier.name.toLowerCase().includes(value.toLowerCase()) ||
        (supplier.contact && supplier.contact.includes(value)) ||
        (supplier.email && supplier.email.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredSuppliers(filtered);
      setShowSupplierDropdown(true);
    } else {
      setShowSupplierDropdown(false);
      // Clear other supplier fields when name is cleared
      setFormData(prev => ({
        ...prev,
        supplierContact: '',
        supplierAddress: '',
        supplierGST: '',
        supplierEmail: ''
      }));
    }
  };

  const handleSupplierSelect = (supplier) => {
    setFormData(prev => ({
      ...prev,
      supplierName: supplier.name,
      supplierContact: supplier.contact || '',
      supplierAddress: supplier.address || '',
      supplierGST: supplier.gstNumber || '',
      supplierEmail: supplier.email || ''
    }));
    setShowSupplierDropdown(false);
  };

  const handleAddSupplier = () => {
    setShowSupplierModal(true);
  };

  const handleSupplierSaved = (savedSupplier) => {
    // Reload suppliers list
    loadSuppliers();
    // Optionally select the newly added supplier
    if (savedSupplier) {
      handleSupplierSelect(savedSupplier);
    }
  };

  // Filter products based on search - Updated to include HSN code search
  useEffect(() => {
    if (!searchTerm) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.partNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.hsnCode?.toLowerCase().includes(searchTerm.toLowerCase()) // Added HSN code search
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  // Add this useEffect after the existing useEffects
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside HSN dropdown
      if (showHsnDropdown && !event.target.closest('.hsn-dropdown-container')) {
        setShowHsnDropdown(false);
        setActiveHsnIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHsnDropdown]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Remove the static commonHsnCodes array completely and replace with this
  const getProductHsnCodes = () => {
    // Get unique HSN codes from your products only
    const productHsnCodes = products
      .filter(product => product.hsnCode && product.hsnCode.trim()) // Only products with HSN codes
      .map(product => ({
        code: product.hsnCode,
        description: product.name,
        productName: product.name,
        productId: product.id,
        rate: product.cost || 0
      }));

    // Remove duplicates based on HSN code
    const uniqueHsnCodes = productHsnCodes.reduce((acc, current) => {
      const existing = acc.find(item => item.code === current.code);
      if (!existing) {
        acc.push(current);
      } else {
        // If HSN exists, combine product names in description
        existing.description = existing.description + `, ${current.productName}`;
      }
      return acc;
    }, []);

    return uniqueHsnCodes;
  };
  // Handle HSN code suggestions
  // Update the handleHsnSearch function
  const handleHsnSearch = (value, index) => {
    console.log('HSN Search called with:', value, index);

    if (value && value.length >= 1) {
      const productHsnCodes = getProductHsnCodes(); // Only from your products

      const filtered = productHsnCodes.filter(hsn =>
        hsn.code.toLowerCase().startsWith(value.toLowerCase()) || // Starts with typed value
        hsn.code.includes(value) || // Contains typed value
        hsn.description.toLowerCase().includes(value.toLowerCase()) // Product name contains value
      );

      console.log('Available product HSN codes:', productHsnCodes);
      console.log('Filtered HSN suggestions:', filtered);

      setHsnSuggestions(filtered);
      setShowHsnDropdown(filtered.length > 0);
      setActiveHsnIndex(index);
    } else {
      setShowHsnDropdown(false);
      setHsnSuggestions([]);
      setActiveHsnIndex(null);
    }
  };

  const handleHsnSelect = (hsnCode, index) => {
    console.log('HSN Selected:', hsnCode, index);

    const newItems = [...items];
    newItems[index].hsnCode = hsnCode.code;

    // Hide dropdown immediately
    setShowHsnDropdown(false);
    setHsnSuggestions([]);
    setActiveHsnIndex(null);

    // Auto-select the product associated with this HSN
    if (hsnCode.productId) {
      const matchingProduct = products.find(p => p.id === hsnCode.productId);
      if (matchingProduct) {
        newItems[index].productId = matchingProduct.id;
        newItems[index].productName = matchingProduct.name;
        newItems[index].rate = matchingProduct.cost || 0;
        newItems[index].gstRate = 18;

        // Calculate amount
        const quantity = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        newItems[index].amount = quantity * rate;
      }
    }

    setItems(newItems);
  };
  // Handle item changes - Updated to handle individual GST rates
  // Update the handleItemChange function - fix the hsnCode case
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];

    if (field === 'quantity') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'rate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'gstRate') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'hsnCode') {
      newItems[index][field] = value;
      // Trigger HSN search immediately while typing
      handleHsnSearch(value, index);

      // If HSN code matches exactly, try to find matching product
      if (value && value.length >= 4) {
        const matchingProduct = products.find(p =>
          p.hsnCode && p.hsnCode === value
        );

        if (matchingProduct) {
          newItems[index].productId = matchingProduct.id;
          newItems[index].productName = matchingProduct.name;
          newItems[index].rate = matchingProduct.cost || 0;
          newItems[index].gstRate = 18;
        }
      }
    } else {
      newItems[index][field] = value;
    }

    // If product selected, populate details
    if (field === 'productId' && value) {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].productName = product.name;
        newItems[index].rate = product.cost || 0;
        newItems[index].hsnCode = product.hsnCode || '';
        newItems[index].gstRate = 18;
      }
    }

    // Calculate amount
    if (field === 'quantity' || field === 'rate') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      newItems[index].amount = quantity * rate;
    }

    setItems(newItems);
  };

  // Add new item
  const addItem = () => {
    setItems([...items, {
      productId: '',
      productName: '',
      quantity: 1,
      rate: 0,
      gstRate: 18, // Default GST rate
      amount: 0,
      hsnCode: ''
    }]);
  };

  // Handle add new product navigation
  // Update the handleAddNewProduct function
  const handleAddNewProduct = () => {
    console.log('Add new product clicked'); // Debug log
    console.log('onNavigateToAddProduct prop:', typeof onNavigateToAddProduct); // Debug log

    if (onNavigateToAddProduct && typeof onNavigateToAddProduct === 'function') {
      console.log('Calling navigation function'); // Debug log
      onNavigateToAddProduct();
    } else {
      console.log('Navigation function not available'); // Debug log
      showAlert('Navigation function not provided', 'warning');
    }
  };

  // Remove item
  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  // Helper function to calculate CGST and SGST
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Calculate totals - Updated for individual GST rates
const calculateTotals = () => {
  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);

  let totalGstAmount = 0;
  let grandTotal = 0;
  
  if (formData.gstType === 'with_gst') {
    items.forEach(item => {
      const itemAmount = parseFloat(item.amount) || 0;
      const gstRate = parseFloat(item.gstRate) || 0;
      const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemAmount, gstRate);
      totalGstAmount += cgstAmount + sgstAmount;
      grandTotal += itemAmount + cgstAmount + sgstAmount;
    });
  } else {
    grandTotal = subtotal;
  }

  return { subtotal, gstAmount: totalGstAmount, total: grandTotal };
};

  // Get GST breakdown by rate
  const getGstBreakdown = () => {
    const gstBreakdown = {};
    items.forEach(item => {
      const rate = item.gstRate || 0;
      const itemAmount = parseFloat(item.amount) || 0;
      const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemAmount, rate);

      if (rate > 0) {
        if (!gstBreakdown[rate]) {
          gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
        }
        gstBreakdown[rate].taxableAmount += itemAmount;
        gstBreakdown[rate].cgstAmount += cgstAmount;
        gstBreakdown[rate].sgstAmount += sgstAmount;
        gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
      }
    });
    return gstBreakdown;
  };

  // Generate invoice number
  const generateInvoiceNumber = () => {
    const prefix = 'PUR';
    const date = new Date();
    const timestamp = date.getTime().toString().slice(-6);
    return `${prefix}-${date.getFullYear()}-${timestamp}`;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Update product stock when purchasing (increase stock)
  const updateProductStockForPurchase = async (items) => {
    const stockUpdates = [];

    try {
      for (const item of items) {
        if (item.productId) {
          const productRef = doc(db, 'products', item.productId);
          const productSnap = await getDoc(productRef);

          if (productSnap.exists()) {
            const currentStock = productSnap.data().stock || 0;
            const newStock = currentStock + parseInt(item.quantity);

            await updateDoc(productRef, {
              stock: newStock,
              updatedAt: Timestamp.fromDate(new Date())
            });

            stockUpdates.push({
              productId: item.productId,
              productName: item.productName,
              previousStock: currentStock,
              newStock: newStock,
              quantityPurchased: item.quantity,
              purchaseAmount: item.amount
            });

            console.log(`Stock updated for ${item.productName}: ${currentStock} -> ${newStock} (purchased: ${item.quantity})`);
          }
        }
      }

      return stockUpdates;
    } catch (error) {
      console.error('Error updating product stock for purchase:', error);
      throw error;
    }
  };

  // Handle form submission for preview
  const handleFormSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate form before preview
    if (!formData.supplierName.trim()) {
      setError('Supplier name is required');
      return;
    }

    if (items.some(item => !item.productName.trim() || item.quantity <= 0 || item.rate <= 0)) {
      setError('All items must have valid product name, quantity, and rate');
      return;
    }

    // Move to preview step
    setStep(2);
  };

  // Handle final submission
  const handleSubmit = async () => {
    setError('');

    if (!vendorProfile?.uid) {
      setError('User not authenticated');
      return;
    }

    try {
      setSubmitting(true);

      const { subtotal, gstAmount, total } = calculateTotals();

      // Generate invoice number if not provided
      const invoiceNumber = formData.invoiceNumber || invoiceNumber();

      // Prepare invoice data
      const invoiceData = {
        type: 'purchase',
        vendorId: vendorProfile.uid,
        supplierName: formData.supplierName.trim(),
        supplierContact: formData.supplierContact.trim(),
        supplierAddress: formData.supplierAddress.trim(),
        supplierGST: formData.supplierGST.trim(),
        supplierEmail: formData.supplierEmail.trim(),
        invoiceNumber: invoiceNumber,
        purchaseDate: formData.purchaseDate,
        paymentTerms: 'immediate',
        notes: formData.notes.trim(),
        gstType: formData.gstType,
        items: items.map(item => ({
          productId: item.productId || null,
          productName: item.productName.trim(),
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate),
          gstRate: parseFloat(item.gstRate),
          amount: parseFloat(item.amount),
          hsnCode: item.hsnCode.trim()
        })),
        subtotal: subtotal,
        totalGstAmount: gstAmount,
        totalAmount: total,
        status: 'received',
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'purchaseInvoices'), invoiceData);

      const createdInvoice = {
        id: docRef.id,
        ...invoiceData
      };

      // Update product stock for existing products
      try {
        const stockUpdates = await updateProductStockForPurchase(items);

        let stockUpdateMessage = `Purchase invoice created successfully! Stock updated for ${stockUpdates.length} products:`;
        stockUpdates.forEach(update => {
          stockUpdateMessage += `\n• ${update.productName}: ${update.previousStock} → ${update.newStock} units`;
        });

        showAlert(stockUpdateMessage, 'success');

      } catch (stockError) {
        console.warn('Purchase invoice created but stock update failed:', stockError);
        showAlert('Purchase invoice created successfully, but stock update failed. Please update stock manually.', 'warning');
      }

      onSubmit?.(createdInvoice);
      onClose?.();

    } catch (error) {
      console.error('Error creating purchase invoice:', error);
      setError('Failed to create purchase invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const { subtotal, gstAmount, total } = calculateTotals();

  // Preview Step
  if (step === 2) {
    const gstBreakdown = getGstBreakdown();

    const content = (
      <div className="bg-white">
        {/* Print Styles */}
       <style jsx>{`
  @media print {
    /* Force exact colors and better rendering */
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      box-sizing: border-box !important;
    }
    
    /* Hide everything first, then show only print content */
    body * {
      visibility: hidden;
    }
    
    .print-content, .print-content * {
      visibility: visible;
    }
    
    /* Print content positioning and sizing */
    .print-content {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 15px !important;
      font-size: 12px !important;
      line-height: 1.3 !important;
      font-family: Arial, sans-serif !important;
      background: white !important;
    }
    
    /* Hide non-print elements */
    .no-print {
      display: none !important;
    }
    
    /* Page setup for A4 */
    @page {
      size: A4;
      margin: 0.4in;
    }
    
    /* Heading sizes - scaled down proportionally */
    .print-content h1 {
      font-size: 24px !important;
      margin: 0 0 10px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h2 {
      font-size: 18px !important;
      margin: 0 0 8px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h3 {
      font-size: 16px !important;
      margin: 0 0 6px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h4 {
      font-size: 14px !important;
      margin: 0 0 4px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h5 {
      font-size: 12px !important;
      margin: 0 0 3px 0 !important;
      font-weight: bold !important;
    }
    
    /* Paragraph and text sizing */
    .print-content p {
      margin: 0 0 3px 0 !important;
      font-size: 11px !important;
    }
    
    .print-content div {
      margin: 0 !important;
    }
    
    /* Table optimizations - keep structure, reduce size */
    .print-content table {
      width: 100% !important;
      border-collapse: collapse !important;
      margin: 8px 0 !important;
      font-size: 10px !important;
      page-break-inside: auto !important;
    }
    
    .print-content thead {
      display: table-header-group !important;
    }
    
    .print-content tbody {
      display: table-row-group !important;
    }
    
    .print-content th {
      background-color: #f8f9fa !important;
      border: 1px solid #dee2e6 !important;
      padding: 4px 3px !important;
      font-size: 9px !important;
      font-weight: bold !important;
      text-align: center !important;
      vertical-align: middle !important;
      line-height: 1.1 !important;
    }
    
    .print-content td {
      border: 1px solid #dee2e6 !important;
      padding: 3px 2px !important;
      font-size: 10px !important;
      vertical-align: top !important;
      line-height: 1.2 !important;
    }
    
    /* Reduce spacing in existing layout classes */
    .print-content .mb-8 {
      margin-bottom: 8px !important;
    }
    
    .print-content .mb-6 {
      margin-bottom: 6px !important;
    }
    
    .print-content .mb-4 {
      margin-bottom: 4px !important;
    }
    
    .print-content .mb-3 {
      margin-bottom: 3px !important;
    }
    
    .print-content .mb-2 {
      margin-bottom: 2px !important;
    }
    
    .print-content .mt-8 {
      margin-top: 8px !important;
    }
    
    .print-content .mt-6 {
      margin-top: 6px !important;
    }
    
    .print-content .mt-4 {
      margin-top: 4px !important;
    }
    
    .print-content .mt-3 {
      margin-top: 3px !important;
    }
    
    .print-content .mt-2 {
      margin-top: 2px !important;
    }
    
    /* Padding reductions */
    .print-content .py-4 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-3 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-2 {
      padding-top: 1px !important;
      padding-bottom: 1px !important;
    }
    
    .print-content .px-4 {
      padding-left: 3px !important;
      padding-right: 3px !important;
    }
    
    .print-content .px-3 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .print-content .px-2 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* Compact existing sections without changing structure */
    .print-content .p-8 {
      padding: 12px !important;
    }
    
    .print-content .p-6 {
      padding: 8px !important;
    }
    
    .print-content .p-4 {
      padding: 6px !important;
    }
    
    /* Text size adjustments */
    .print-content .text-3xl {
      font-size: 24px !important;
    }
    
    .print-content .text-2xl {
      font-size: 20px !important;
    }
    
    .print-content .text-xl {
      font-size: 18px !important;
    }
    
    .print-content .text-lg {
      font-size: 16px !important;
    }
    
    .print-content .text-base {
      font-size: 12px !important;
    }
    
    .print-content .text-sm {
      font-size: 11px !important;
    }
    
    .print-content .text-xs {
      font-size: 9px !important;
    }
    
    /* Ensure colors print properly */
    .print-content .text-gray-900 {
      color: #000 !important;
    }
    
    .print-content .text-gray-800 {
      color: #1f2937 !important;
    }
    
    .print-content .text-gray-700 {
      color: #374151 !important;
    }
    
    .print-content .text-gray-600 {
      color: #4b5563 !important;
    }
    
    .print-content .text-gray-500 {
      color: #6b7280 !important;
    }
    
    .print-content .text-purple-900 {
      color: #581c87 !important;
    }
    
    .print-content .text-red-600 {
      color: #dc2626 !important;
    }
    
    .print-content .text-orange-600 {
      color: #ea580c !important;
    }
    
    .print-content .text-green-600 {
      color: #16a34a !important;
    }
    
    .print-content .text-blue-600 {
      color: #2563eb !important;
    }
    
    /* Background colors for sections */
    .print-content .bg-gray-50 {
      background-color: #f9fafb !important;
    }
    
    .print-content .bg-blue-50 {
      background-color: #eff6ff !important;
    }
    
    .print-content .bg-purple-50 {
      background-color: #faf5ff !important;
    }
    
    .print-content .bg-yellow-50 {
      background-color: #fefce8 !important;
    }
    
    /* Borders */
    .print-content .border {
      border: 1px solid #d1d5db !important;
    }
    
    .print-content .border-gray-200 {
      border-color: #e5e7eb !important;
    }
    
    .print-content .border-gray-300 {
      border-color: #d1d5db !important;
    }
    
    .print-content .border-t {
      border-top: 1px solid #d1d5db !important;
    }
    
    .print-content .border-b {
      border-bottom: 1px solid #d1d5db !important;
    }
    
    .print-content .border-l {
      border-left: 1px solid #d1d5db !important;
    }
    
    .print-content .border-r {
      border-right: 1px solid #d1d5db !important;
    }
    
    /* Flex layouts - keep structure */
    .print-content .flex {
      display: flex !important;
    }
    
    .print-content .justify-between {
      justify-content: space-between !important;
    }
    
    .print-content .items-center {
      align-items: center !important;
    }
    
    .print-content .items-start {
      align-items: flex-start !important;
    }
    
    .print-content .space-x-6 > * + * {
      margin-left: 10px !important;
    }
    
    .print-content .space-x-3 > * + * {
      margin-left: 6px !important;
    }
    
    .print-content .space-x-2 > * + * {
      margin-left: 4px !important;
    }
    
    .print-content .space-y-2 > * + * {
      margin-top: 2px !important;
    }
    
    .print-content .space-y-1 > * + * {
      margin-top: 1px !important;
    }
    
    /* QR code sizing */
    .print-content img {
      max-width: 100% !important;
      height: auto !important;
    }
    
    /* Specific QR code adjustments */
    .print-content .w-48 {
      width: 80px !important;
    }
    
    .print-content .h-40 {
      height: 60px !important;
    }
    
    /* Grid layouts */
    .print-content .grid {
      display: grid !important;
    }
    
    .print-content .grid-cols-1 {
      grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
    }
    
    .print-content .grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    
    .print-content .gap-6 {
      gap: 8px !important;
    }
    
    .print-content .gap-4 {
      gap: 6px !important;
    }
    
    /* Badges and small elements */
    .print-content .rounded-full {
      border-radius: 9999px !important;
    }
    
    .print-content .rounded-md {
      border-radius: 6px !important;
    }
    
    .print-content .rounded {
      border-radius: 4px !important;
    }
    
    /* Font weights */
    .print-content .font-bold {
      font-weight: 700 !important;
    }
    
    .print-content .font-semibold {
      font-weight: 600 !important;
    }
    
    .print-content .font-medium {
      font-weight: 500 !important;
    }
    
    /* Ensure table responsiveness is disabled in print */
    .print-content .overflow-x-auto {
      overflow-x: visible !important;
    }
    
    /* Make sure flex items don't shrink too much */
    .print-content .flex-1 {
      flex: 1 1 0% !important;
    }
    
    .print-content .flex-shrink-0 {
      flex-shrink: 0 !important;
    }
    
    /* Text alignment */
    .print-content .text-left {
      text-align: left !important;
    }
    
    .print-content .text-center {
      text-align: center !important;
    }
    
    .print-content .text-right {
      text-align: right !important;
    }
    
    /* Width constraints */
    .print-content .w-full {
      width: 100% !important;
    }
    
    .print-content .w-96 {
      width: 300px !important;
    }
    
    /* Handle very long content */
    .print-content .whitespace-pre-wrap {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }
    
    /* Ensure content doesn't break poorly */
    .print-content .page-break-inside-avoid {
      page-break-inside: avoid !important;
    }
    
    /* Make sure all text is readable */
    .print-content {
      color: #000 !important;
    }
    
    /* Specific adjustments for very small elements */
    .print-content .min-w-full {
      min-width: 100% !important;
    }
    
    .print-content .divide-y > * {
      border-top-width: 1px !important;
      border-color: #e5e7eb !important;
    }
    
    .print-content .divide-gray-200 > * {
      border-color: #e5e7eb !important;
    }
  }
`}</style>

        {/* Header Actions */}
        <div className="flex justify-between items-center mb-6 no-print">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Purchase Invoice Preview</h3>

          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Edit
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print
            </button>
            {isModal && (
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md no-print">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="print-content bg-white border border-gray-200 rounded-lg p-8">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">PURCHASE INVOICE</h1>
              <div className="text-sm text-gray-600">
               <p><strong>Invoice #:</strong> {formData.invoiceNumber || invoiceNumber}</p>
                <p><strong>Purchase Date:</strong> {formatDate(formData.purchaseDate)}</p>
              </div>
            </div>

            {/* Vendor Details (Buyer) */}
            <div className="text-right">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {vendorProfile?.shopName || 'Your Shop Name'}
              </h2>
              <div className="text-sm text-gray-600">
                <p>{vendorProfile?.address || 'Shop Address'}</p>
                <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                <p>Email: {vendorProfile?.email || 'N/A'}</p>
                {vendorProfile?.gstNumber && (
                  <p>GST: {vendorProfile.gstNumber}</p>
                )}
              </div>
            </div>
          </div>

          {/* Supplier Details */}
          {formData.gstType === 'with_gst' && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Supplier Details:</h3>
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{formData.supplierName}</p>
                  {formData.supplierContact && <p className="text-gray-600">{formData.supplierContact}</p>}
                  {formData.supplierEmail && <p className="text-gray-600">{formData.supplierEmail}</p>}
                  {formData.supplierGST && <p className="text-gray-600">GST: {formData.supplierGST}</p>}
                  {formData.supplierAddress && <p className="text-gray-600 mt-2">{formData.supplierAddress}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Purchase Items</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Description
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      HSN
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Rate (₹)
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      GST (%)
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      CGST (₹)
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      SGST (₹)
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount (₹)
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Total(₹)
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.filter(item => item.productName.trim() && item.quantity > 0 && item.rate > 0).map((item, index) => {
                    const itemAmount = parseFloat(item.amount) || 0;
                    const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemAmount, item.gstRate || 0);

                    return (
                      <tr key={index}>
                        <td className="px-3 py-4 border-r">
                          <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                          {item.productId && (
                            <div className="text-xs text-gray-500">ID: {item.productId}</div>
                          )}
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.quantity}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                        </td>
                        <td className="px-3 py-4 text-right">
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                        </td>
                        <td className="px-3 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(itemAmount + cgstAmount + sgstAmount)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end mb-8">
            <div className="w-96">
              <div className="bg-gray-50 p-6 rounded-md border">
                <div className="space-y-2">
                  <div className="flex justify-between py-1">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                  </div>

                  {formData.gstType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                    <div className="border-t border-gray-200 pt-2">
                      <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                      {Object.entries(gstBreakdown).map(([rate, data]) => (
                        <div key={rate} className="space-y-1 mb-2">
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                            <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between py-1 border-t border-gray-200 mt-1 pt-1">
                        <span className="text-sm text-gray-600">Total GST:</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(gstAmount)}</span>
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(total)}</span>
                    </div>

                    {/* Show invoice type badge */}
                    <div className="mt-2 flex justify-end">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formData.gstType === 'with_gst'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        {formData.gstType === 'with_gst' ? 'GST Invoice' : 'Non-GST Invoice'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {formData.notes && (
            <div className="mb-8">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h5>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p>This is a computer generated purchase invoice.</p>
              <p className="mt-2">Received by: {vendorProfile?.shopName || 'Your Shop Name'}</p>
            </div>
          </div>
        </div>

        {/* Preview Actions */}
        <div className="flex justify-end space-x-3 mt-6 no-print">
          <button
            type="button"
            onClick={() => setStep(1)}
            disabled={submitting}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Edit Invoice
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:opacity-50 flex items-center"
          >
            {submitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Create & Update Stock
              </>
            )}
          </button>
        </div>
      </div>
    );

    if (isModal) {
      return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
            {content}
          </div>
        </div>
      );
    }

    return <div className="p-6">{content}</div>;
  }

  // Form Step (Step 1)
  const content = (
    <div className="bg-white">
      {/* Header Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Create Purchase Invoice</h3>
        </div>
        {isModal && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleFormSubmit}>
        <div className="bg-white border border-gray-200 rounded-lg p-8">
          {/* Invoice Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">PURCHASE INVOICE</h1>
              <div className="text-sm text-gray-600">
 <p><strong>Invoice #:</strong> {formData.invoiceNumber || invoiceNumber}</p>
                <p><strong>Purchase Date:</strong> {formatDate(formData.purchaseDate)}</p>
              </div>
            </div>

            {/* Vendor Details (Buyer) */}
            <div className="text-right">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {vendorProfile?.shopName || 'Your Shop Name'}
              </h2>
              <div className="text-sm text-gray-600">
                <p>{vendorProfile?.address || 'Shop Address'}</p>
                <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                <p>Email: {vendorProfile?.email || 'N/A'}</p>
                {vendorProfile?.gstNumber && (
                  <p>GST: {vendorProfile.gstNumber}</p>
                )}
              </div>
            </div>
          </div>

          {/* Purchase Configuration */}
          <div className="mb-6 bg-blue-50 p-4 rounded-lg">
            <h4 className="text-md font-medium text-gray-900 mb-3">Purchase Configuration</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">GST Type</label>
                <select
                  name="gstType"
                  value={formData.gstType}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                >
                  <option value="with_gst">With GST</option>
                  <option value="without_gst">Without GST</option>
                </select>
              </div>
            </div>
          </div>

          {/* Supplier Details */}
          {formData.gstType === 'with_gst' && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Supplier Details:</h3>
                <button
                  type="button"
                  onClick={handleAddSupplier}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Supplier
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border-2 border-dashed border-gray-300">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Supplier Name *</label>
                    <input
                      type="text"
                      name="supplierName"
                      value={formData.supplierName}
                      onChange={(e) => handleSupplierSearch(e.target.value)}
                      onFocus={() => {
                        if (formData.supplierName.trim() && filteredSuppliers.length > 0) {
                          setShowSupplierDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay hiding dropdown to allow clicking on options
                        setTimeout(() => setShowSupplierDropdown(false), 200);
                      }}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="Search or enter supplier name"
                    />

                    {/* Supplier Dropdown */}
                    {showSupplierDropdown && filteredSuppliers.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredSuppliers.map((supplier) => (
                          <div
                            key={supplier.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-orange-50"
                            onClick={() => handleSupplierSelect(supplier)}
                          >
                            <div className="flex items-center">
                              <span className="font-medium block truncate">
                                {supplier.name}
                              </span>
                            </div>
                            <div className="text-gray-500 text-xs">
                              {supplier.contact && <span>{supplier.contact}</span>}
                              {supplier.contact && supplier.email && <span> • </span>}
                              {supplier.email && <span>{supplier.email}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Number</label>
                    <input
                      type="text"
                      name="supplierContact"
                      value={formData.supplierContact}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="Phone or email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="supplierEmail"
                      value={formData.supplierEmail}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="supplier@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">GST Number</label>
                    <input
                      type="text"
                      name="supplierGST"
                      value={formData.supplierGST}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="Supplier GST number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Date *</label>
                    <input
                      type="date"
                      name="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Supplier Address</label>
                  <textarea
                    name="supplierAddress"
                    value={formData.supplierAddress}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Enter supplier's complete address"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </div>
            </div>
          )}

          {formData.gstType === 'without_gst' && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Supplier Details:</h3>
                <button
                  type="button"
                  onClick={handleAddSupplier}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Supplier
                </button>
              </div>
              <div className="bg-gray-50 p-4 rounded-md border-2 border-dashed border-gray-300">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700">Supplier Name *</label>
                    <input
                      type="text"
                      name="supplierName"
                      value={formData.supplierName}
                      onChange={(e) => handleSupplierSearch(e.target.value)}
                      onFocus={() => {
                        if (formData.supplierName.trim() && filteredSuppliers.length > 0) {
                          setShowSupplierDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay hiding dropdown to allow clicking on options
                        setTimeout(() => setShowSupplierDropdown(false), 200);
                      }}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      placeholder="Search or enter supplier name"
                    />

                    {/* Supplier Dropdown */}
                    {showSupplierDropdown && filteredSuppliers.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredSuppliers.map((supplier) => (
                          <div
                            key={supplier.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-orange-50"
                            onClick={() => handleSupplierSelect(supplier)}
                          >
                            <div className="flex items-center">
                              <span className="font-medium block truncate">
                                {supplier.name}
                              </span>
                            </div>
                            <div className="text-gray-500 text-xs">
                              {supplier.contact && <span>{supplier.contact}</span>}
                              {supplier.contact && supplier.email && <span> • </span>}
                              {supplier.email && <span>{supplier.email}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Purchase Date *</label>
                    <input
                      type="date"
                      name="purchaseDate"
                      value={formData.purchaseDate}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Invoice Number</label>
                  <input
                    type="text"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Items Table */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-900">Purchase Items</h4>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Item
                </button>
                <button
                  type="button"
                  onClick={handleAddNewProduct}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                >
                  <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add New Product
                </button>
              </div>
            </div>

            <div className="overflow-x-auto table-container" style={{ position: 'relative', overflow: 'visible' }}>
              <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Description
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      HSN
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Qty
                    </th>
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Rate (₹)
                    </th>
                    {formData.gstType === 'with_gst' && (
                      <>
                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          GST (%)
                        </th>
                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          CGST (₹)
                        </th>
                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                          SGST (₹)
                        </th>
                      </>
                    )}
                    <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Amount (₹)
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">
                      Total Amount (₹)
                    </th>
                    <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item, index) => {
                    const itemAmount = parseFloat(item.amount) || 0;
                    const { cgstAmount, sgstAmount } = formData.gstType === 'with_gst' ?
                      calculateCGSTSGST(itemAmount, item.gstRate || 0) :
                      { cgstAmount: 0, sgstAmount: 0 };

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-4 border-r">
                          <select
                            value={item.productId}
                            onChange={(e) => handleItemChange(index, 'productId', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          >
                            <option value="">Select a product</option>
                            {filteredProducts.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.name} ({product.partNumber || product.sku || 'No SKU'})
                                {product.hsnCode && ` - HSN: ${product.hsnCode}`}
                              </option>
                            ))}
                          </select>
                          {item.productId && (
                            <div className="text-xs text-gray-500 mt-1">
                              ID: {item.productId}
                            </div>
                          )}
                        </td>

                        <td className="px-2 py-4 text-center border-r relative">
                          <input
                            type="text"
                            value={item.hsnCode}
                            onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                            onFocus={() => {
                              setActiveHsnIndex(index);
                              if (item.hsnCode && item.hsnCode.length >= 1) {
                                handleHsnSearch(item.hsnCode, index);
                              }
                            }}
                            onBlur={() => {
                              // Delay hiding to allow clicking on suggestions
                              setTimeout(() => {
                                setShowHsnDropdown(false);
                                setActiveHsnIndex(null);
                              }, 200);
                            }}
                            placeholder="HSN"
                            className="w-20 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          />

                          {/* HSN Suggestions Dropdown - Fixed positioning */}
                          {showHsnDropdown && activeHsnIndex === index && hsnSuggestions.length > 0 && (
                            <div
                              className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-auto z-50"
                              style={{ minWidth: '280px' }}
                            >
                              {hsnSuggestions.map((hsn, suggestionIndex) => (
                                <div
                                  key={suggestionIndex}
                                  className="cursor-pointer px-3 py-2 hover:bg-orange-50 border-b border-gray-100 last:border-b-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent input blur
                                    handleHsnSelect(hsn, index);
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium text-gray-900 text-sm">{hsn.code}</span>
                                    <span className="text-xs text-gray-600 mt-1">{hsn.description}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-4 text-center border-r">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </td>
                        <td className="px-2 py-4 text-right border-r">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                            className="w-20 px-1 py-1 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </td>
                        {formData.gstType === 'with_gst' && (
                          <>
                            <td className="px-2 py-4 text-center border-r relative">
                              <div className="relative inline-block">
                                <select
                                  value={item.gstRate || 0}
                                  onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                                  className="appearance-none w-20 px-2 py-1 pr-6 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer"
                                >
                                  <option value={0}>0%</option>
                                  <option value={3}>3%</option>
                                  <option value={5}>5%</option>
                                  <option value={12}>12%</option>
                                  <option value={18}>18%</option>
                                  <option value={28}>28%</option>
                                </select>

                              </div>
                            </td>
                            <td className="px-2 py-4 text-right border-r">
                              <span className="text-xs font-medium text-gray-900">
                                {formatCurrency(cgstAmount)}
                              </span>
                            </td>
                            <td className="px-2 py-4 text-right border-r">
                              <span className="text-xs font-medium text-gray-900">
                                {formatCurrency(sgstAmount)}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="px-2 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(item.amount)}
                          </span>
                        </td>
                        <td className="px-3 py-4 text-right border-r">
                          <span className="text-sm font-medium text-gray-900">
                            {formatCurrency(itemAmount + cgstAmount + sgstAmount)}
                          </span>
                        </td>
                        <td className="px-2 py-4 text-center">
                          {items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-900"
                              title="Remove item"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section */}
          <div className="flex justify-end mb-8">
            <div className="w-96">
              <div className="bg-gray-50 p-6 rounded-md border">
                <div className="space-y-2">
                  <div className="flex justify-between py-1">
                    <span className="text-sm text-gray-600">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                  </div>

                  {formData.gstType === 'with_gst' && (
                    <div className="flex justify-between py-1">
                      <span className="text-sm text-gray-600">Total GST:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(gstAmount)}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between">
                      <span className="text-lg font-semibold text-gray-900">Total:</span>
                      <span className="text-lg font-bold text-gray-900">{formatCurrency(total)}</span>
                    </div>

                    {/* Show invoice type badge */}
                    <div className="mt-2 flex justify-end">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${formData.gstType === 'with_gst'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                        }`}>
                        {formData.gstType === 'with_gst' ? 'GST Invoice' : 'Non-GST Invoice'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <label className="block text-sm font-semibold text-gray-900 mb-2">Notes:</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
              placeholder="Additional notes about this purchase..."
            />
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6">
            <div className="text-center text-sm text-gray-500">
              <p>This is a computer generated purchase invoice.</p>
              <p className="mt-2">Received by: {vendorProfile?.shopName || 'Your Shop Name'}</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!formData.supplierName.trim() || items.every(item => !item.productName.trim())}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
          >
            Preview Invoice
          </button>
        </div>
      </form>

      {/* Supplier Modal */}
      <SupplierModal
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSave={handleSupplierSaved}
      />
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
          {content}
        </div>
      </div>
    );
  }

  return <div className="p-6">{content}</div>;
};

export default CreatePurchaseInvoiceModal;